import React from 'react';
import type {
  HeroBlock as HeroBlockType,
  ImageData,
  HeroImageData,
} from '@lnr-v2/types';
import { getTransformedImageUrl } from '../../../../utils/imageHelpers';
import styles from './HeroBlock.module.css';

interface HeroBlockProps {
  block: HeroBlockType;
}

/**
 * Gets the best image URL for display, supporting both:
 * - HeroImageData (new format: url from CDN, high quality)
 * - ImageData (legacy format: base64 full/thumbnail)
 */
function getHeroImageUrl(
  image: ImageData | HeroImageData | undefined
): string | null {
  if (!image) return null;

  // New HeroImageData format (CDN URL)
  if ('url' in image && image.url) {
    return image.url;
  }

  // Legacy ImageData format (base64)
  if ('full' in image && image.full) {
    return image.full;
  }

  return null;
}

/**
 * Gets the preview/thumbnail for poster or fallback
 */
function getHeroPreview(
  image: ImageData | HeroImageData | undefined
): string | null {
  if (!image) return null;

  // New HeroImageData format
  if ('preview' in image && image.preview) {
    return image.preview;
  }

  // Legacy ImageData format
  if ('thumbnail' in image && image.thumbnail) {
    return image.thumbnail;
  }

  return null;
}

/**
 * Capitalizes the first letter of a string
 */
const capitalize = (str: string): string =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const HeroBlock: React.FC<HeroBlockProps> = ({ block }) => {
  const { content, settings } = block;

  // Height variant class
  const heightVariant = settings?.height || 'medium';
  const heightClass =
    styles[`height${capitalize(heightVariant)}` as keyof typeof styles] || '';

  // Alignment variant class
  const alignVariant = settings?.alignment || 'center';
  const alignClass =
    styles[`align${capitalize(alignVariant)}` as keyof typeof styles] || '';

  // Build section classes
  const sectionClasses = [styles.hero, heightClass].filter(Boolean).join(' ');

  // Build content classes
  const contentClasses = [styles.content, styles.contentAbsolute, alignClass]
    .filter(Boolean)
    .join(' ');

  // Get the appropriate image URLs (supports both new CDN and legacy base64)
  console.log('HeroBlock - content.image:', content);
  const rawImageUrl = getHeroImageUrl(content.image);
  const imageUrl = getTransformedImageUrl(rawImageUrl);
  const rawMobileImageUrl = getHeroImageUrl(content.mobileImage);
  const mobileImageUrl = getTransformedImageUrl(rawMobileImageUrl);
  const posterUrl =
    getHeroPreview(content.mobileImage) ||
    getHeroPreview(content.image) ||
    mobileImageUrl ||
    imageUrl;

  return (
    <section className={sectionClasses}>
      <div className={styles.heroImageContainer}>
        {/* Background Video */}
        {content.videoUrl && (
          <video
            src={content.videoUrl}
            poster={posterUrl || undefined}
            className={styles.videoBackground}
            loop
            muted
            playsInline
            controls
          />
        )}

        {/* Background Image (only if no video or as fallback) */}
        {!content.videoUrl && imageUrl && (
          <div className={styles.imageWrapper}>
            <picture>
              {mobileImageUrl && (
                <source media="(max-width: 768px)" srcSet={mobileImageUrl} />
              )}
              <source media="(min-width: 769px)" srcSet={imageUrl} />
              <img
                src={imageUrl}
                alt={
                  content.mobileImage?.alt ||
                  content.image?.alt ||
                  content.title ||
                  'Hero'
                }
                loading="eager"
                decoding="async"
              />
            </picture>
          </div>
        )}

        {/* Overlay */}
        {settings?.overlayOpacity !== undefined &&
          settings.overlayOpacity > 0 && (
            <div
              className={styles.overlay}
              style={{ opacity: settings.overlayOpacity / 100 }}
              aria-hidden="true"
            />
          )}

        {/* Content */}
        <div className={contentClasses}>
          {content.title && <h1 className={styles.title}>{content.title}</h1>}
          {content.subtitle && (
            <p className={styles.subtitle}>{content.subtitle}</p>
          )}
        </div>
      </div>
    </section>
  );
};
